import styled, { css } from 'styled-components'
import { themeGet } from 'styled-system'
import { BaseContainer, BodyCopy } from 'secondstep-components'
import { getHighlightColor } from 'utils/highschoolHelpers'
import TextLinkWithFileIcon from 'components/TextLinkWithFileIcon'

export const ContentWrapper = styled.div`
  width: 38.75rem;
  align-self: ${props => (!props.isHighSchool ? 'baseline' : 'auto')};
  @media screen and (max-width: 660px) {
    // avoid text overflowing container:
    // when screen is narrower than 620px + 20px padding on both sides
    width: unset;
    max-width: 100%;
  }
`

export const Spacer = styled.div`
  width: 100%;
  // height is a number in Contentful specifying pixel height:
  height: ${props => props.height}px;
`

export const OuterContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: white;
  border: 1.5px solid
    ${props =>
      props.isHighSchool
        ? getHighlightColor(props.audience)
        : themeGet('individualResource.metaData.h4.backgroundColor')};
  border-radius: 0.5rem;
  margin: -3.6875rem 0 1.3rem;
  // breakpoints.large (1366px) + margin:
  @media screen and (max-width: 1400px) {
    width: calc(100% - 2.5rem);
  }
`

export const StyledContainer = styled(BaseContainer)`
  margin-top: 0;
  display: flex;
  flex-direction: ${props => {
    if (props.isHighSchool) return 'column'
    if (props.hasButton && props.hasLanguageToggle) return 'column'
    if (props.hasButton && !props.hasLanguageToggle) return 'row'
    return 'row'
  }};
  align-items: center;
  width: 100%;
  background-color: white;
  padding: 1.5rem 1.75rem 1.3rem 1.25rem;
  line-height: 1.5rem;
  ${themeGet('breakpoints.tablet')} {
    padding: 1.5rem 3.375rem 1rem 3.375rem;
  }
  ${themeGet('breakpoints.mobileLarge')} {
    padding: 1.25rem;
    padding-bottom: 2.5rem;
  }
  li::marker {
    color: ${themeGet('colors.markerOverride')};
  }
  li:not(:last-child) {
    margin-bottom: 0.5rem;
  }
  h2,
  h3,
  h4 {
    font-family: ${themeGet('font.komet')};
    margin: 0;
  }
  h2 {
    color: ${themeGet('colors.heading.level.2')};
    font-weight: ${themeGet('individualResource.activityDetails.h2.weight')};
    font-size: ${themeGet('individualResource.activityDetails.h2.size')};
    font-family: ${themeGet('individualResource.activityDetails.h2.family')};
    padding-top: ${props => (props.isHighSchool ? '0.5rem' : '1.25rem')};
    padding-bottom: ${props => (props.isHighSchool ? '0.695rem' : '0')};
  }
  h3,
  h4 {
    font-weight: bold;
  }
  h3 {
    color: ${themeGet('colors.heading.level.3')};
    font-size: 1.25rem;
    padding-top: 0.875rem;
    padding-bottom: 0.75rem;
  }
  h4 {
    color: ${themeGet('colors.heading.level.4')};
    font-size: 1.0625rem;
    padding-top: 0.375rem;
    padding-bottom: 0.35rem;
  }
  ${BodyCopy} {
    padding-bottom: ${props => (props.isHighSchool ? '1.1725rem' : '0')};
  }
  [class*='r-marginTop'] {
    margin-top: 0;
  }
  a,
  a:visited {
    text-decoration: none;
    color: ${themeGet('individualResource.activityDetails.a.color')};
  }
  a:focus,
  a:hover {
    color: ${themeGet('individualResource.activityDetails.a.hover.color')};
  }
  p,
  li,
  span {
    line-height: 1.5rem;
  }
  p:not(:last-child) {
    margin-block-end: 0.87rem;
  }
  sup {
    color: ${themeGet('colors.hsPrimaryPink')};
  }
  ul,
  ol {
    margin-block-end: 0.87rem;
    padding-left: 1.5rem;
    margin-bottom: ${props => (props.isHighSchool ? '0.87rem' : '0')};
  }
  ol {
    list-style-position: outside;
  }
  [class*='ImageCalloutButton'] {
    padding-bottom: 1.75rem;
  }
`

export const StyledTextLinkWithFileIcon = styled(TextLinkWithFileIcon)`
  margin-bottom: 0.5rem;
  color: ${themeGet('colors.hsPrimaryPink')};
  fill: ${themeGet('colors.hsPrimaryPink')};
  :hover {
    color: ${themeGet('colors.highschool.green1')};
    fill: ${themeGet('colors.highschool.green1')};
  }
`
export const HeaderContainer = styled.div`
  align-self: baseline;
  font-family: ${themeGet('individualResource.h4.fontFamily')};
  font-size: 1.25rem;
  color: ${themeGet('individualResource.h4.color')};
  padding-top: 1.3rem;
  padding-bottom: 1.6rem;
  background-color: ${themeGet(
    'individualResource.metaData.h4.backgroundColor',
  )};
  width: calc(100% - 1.5625rem);
  padding-left: 1.5625rem;
  border-radius: 0.4rem 0.4rem 0 0;
`

export const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: ${props =>
    props.hasLanguageToggle ? 'space-between' : 'flex-end'};
  width: 100%;
  margin-bottom: 1.3125rem;
`

/* Shared selected button styles */
const selectedButtonStyles = css`
  background-color: ${themeGet(
    'colors.languageToggle.backgroundColor',
  )} !important;
  color: white !important;
  text-decoration: none !important;
  border-radius: 0.875rem;
  z-index: 3;
  min-width: 5.125rem;
  height: 1.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${themeGet(
      'colors.languageToggle.backgroundColor',
    )} !important;
    color: white !important;
    text-decoration: none !important;
  }
`

/* Shared unselected button styles */
const unselectedButtonStyles = css`
  color: ${themeGet('colors.languageToggle.color')};

  &:hover {
    color: ${themeGet('colors.languageToggle.hoverColor')};
  }
`

export const LanguageToggleWrapper = styled.div`
  width: 11rem;
  height: 2.25rem;
  border-radius: 1.125rem;
  background-color: #eef0f2;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 0.25rem;

  /* Style the LinksBox container */
  > div {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0;
  }

  /* Override all button styles - match ButtonToggle approach */
  button {
    position: relative;
    z-index: 2;
    border-radius: 0.875rem;
    background-color: transparent !important;
    color: inherit;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    padding: 0.2rem 1rem;
    border: none;
    transition: all 0.2s ease;
    text-decoration: none !important;
    cursor: pointer;
    font-family: ${themeGet('fontFamilySemibold')};
    line-height: normal;
    white-space: nowrap;
    min-width: 5.125rem;
    height: 1.75rem;
  }

  /* When English is selected, style first button as selected and last button as unselected */
  ${props =>
    props.locale?.startsWith('en') &&
    css`
      button:first-child {
        ${selectedButtonStyles}
      }
      button:last-child {
        ${unselectedButtonStyles}
      }
    `}

  /* When Spanish is selected, style last button as selected and first button as unselected */
  ${props =>
    props.locale?.startsWith('es') &&
    css`
      button:last-child {
        ${selectedButtonStyles}
      }
      button:first-child {
        ${unselectedButtonStyles}
      }
    `}

  /* Hide pipe icon in this instance */
  svg {
    display: none;
  }
`

export const StyledActivityButton = styled.button`
  background: ${themeGet('colors.buttons.primary.bgActive')};
  color: ${themeGet('colors.buttons.primary.titleActive')};
  border-radius: 0.5rem;
  padding: 1.3125rem;
  padding-top: 0.688rem;
  padding-bottom: 0.563rem;
  margin-bottom: 1.3125rem;
  font-family: ${themeGet('font.molde.medium')};
  font-size: 1rem;
  text-align: center;
  border: none;
  cursor: pointer;
  align-self: baseline;
  margin-left: auto;
  min-width: 7.5rem;
  ${themeGet('breakpoints.mobileLarge')} {
    border-radius: 0.3rem;
    margin-bottom: 2.55rem;
  }

  :hover {
    background: ${themeGet(
      'individualResource.activityDetails.button.hover.color',
    )};
    color: ${themeGet('colors.buttons.primary.titleHover')};
  }

  :focus {
    outline: 2px solid
      ${themeGet('individualResource.teachButton.focusOutline')};
    outline-offset: 2px;
  }
`
